import { Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, filter } from 'rxjs';

export interface Breadcrumb {
  label: string;
  url: string;
  fullLabel?: string;
}

@Injectable({
  providedIn: 'root',
})
export class BreadcrumbService {
  currentUrl$: BehaviorSubject<string> = new BehaviorSubject('');
  breadcrumbs: Breadcrumb[] = [];

  constructor(private router: Router, private activatedRoute: ActivatedRoute) {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.breadcrumbs = this.createBreadcrumbs(this.activatedRoute.root);
      });
  }

  private createBreadcrumbs(
    route: ActivatedRoute,
    url: string = '',
    breadcrumbs: Breadcrumb[] = []
  ): Breadcrumb[] {
    const children: ActivatedRoute[] = route.children;

    if (children.length === 0) {
      return breadcrumbs;
    }

    for (const child of children) {
      const routeURL: string = child.snapshot.url
        .map((segment) => segment.path)
        .join('/');
      if (routeURL !== '') {
        url += `/${routeURL}`;
      }

      this.currentUrl$.next(url);

      if (child.snapshot.data && child.snapshot.data['breadcrumb']) {
        breadcrumbs.push({
          label: child.snapshot.data['breadcrumb'],
          url: url,
        });
      }

      this.createBreadcrumbs(child, url, breadcrumbs);
    }

    return breadcrumbs;
  }

  updateLastBreadcrumb(label: string): void {
    if (this.breadcrumbs.length > 0) {
      const maxLength = 30;
      const truncatedLabel = label.length > maxLength
        ? label.substring(0, maxLength) + '...'
        : label;
      this.breadcrumbs[this.breadcrumbs.length - 1].label = truncatedLabel;
      this.breadcrumbs[this.breadcrumbs.length - 1].fullLabel = label;
    }
  }

  removeFirstBreadcrumb(): void {
    if (this.breadcrumbs.length > 0) {
      this.breadcrumbs.shift();
    }
  }

  removeLastBreadcrumb(): void {
    if (this.breadcrumbs.length > 0) {
      this.breadcrumbs.pop();
    }
  }

  addBreadcrumb(label: string): void {
    const maxLength = 30;
    const truncatedLabel = label.length > maxLength
      ? label.substring(0, maxLength) + '...'
      : label;

    this.breadcrumbs.push({
      label: truncatedLabel,
      url: '',
      fullLabel: label
    });
  }
}
